import { Trans, useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import ModalDialog from '../modal-dialog/modal-dialog.component';
import { HeaderSecondary, LinkSmall } from '../typography/typography.styles';
import Button from '../button/button.component';
import { useStore } from '../../stores/store';
import { ModalCrossSaleContainer } from './styles';
import { useIsMobile } from '../../hooks/use-is-mobile.hook';
import Image from '../image-component/image.component';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const Links = [
  {
    lang: 'en',
    privacyPolicy:
      'https://global.meest.shopping/eu/privacy-and-personal-data-processing-policy',
    publicOffer: 'https://global.meest.shopping/eu/public-offer',
  },
  {
    lang: 'uk',
    privacyPolicy: 'https://meest.shopping/ua/privacy_policy',
    publicOffer: 'https://meest.shopping/ua/public_offer_agreement',
  },
];

const CrossSaleModal = ({ isOpen, onClose }: Props) => {
  const {
    localizationsStore: { selectedLocalization },
  } = useStore();

  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const handleContinueClick = () => {
    onClose();
  };

  const bannerSrc = useMemo(() => {
    const src = `${window.location.origin}/download/banners/cross-sale/${isMobile ? 'mobile' : 'desktop'}`;
    let lngCode = 'en';
    if (
      selectedLocalization &&
      ['en', 'uk', 'ru'].includes(selectedLocalization.code)
    ) {
      lngCode = selectedLocalization.code;
    }

    return `${src}/${lngCode}/banner.png`;
  }, [isMobile, selectedLocalization]);

  const localizedLinks = useMemo(
    () =>
      Links.find((x) => x.lang === selectedLocalization?.code) ??
      Links.find((x) => x.lang === 'en'),
    [selectedLocalization]
  );

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      padding='0'
      paddingMobile='0'
      displayButtons={false}>
      <ModalCrossSaleContainer>
        <Image
          alt='Marketing banner with text "Online shopping with benefits"'
          src={bannerSrc}
          width={isMobile ? '377px' : '600px'}
          height={isMobile ? '250px' : '300px'}
          borderRadius='0'
        />
        <div className='textContentWrapper'>
          <HeaderSecondary $size='big'>
            {t('cross_sale_modal_title')}
          </HeaderSecondary>
          <p className='text'>{t('cross_sale_modal_text')}</p>
          <p className='links'>
            {' '}
            <Trans
              i18nKey='cross_sale_modal_agree_links'
              components={{
                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                1: <LinkSmall target='_blank' />,
              }}
              values={{
                publicOfferLink: localizedLinks?.publicOffer,
                privacyLink: localizedLinks?.privacyPolicy,
              }}
            />
          </p>
          <Button
            target='_blank'
            href='https://meest.shopping/ua'
            type='submit'
            $mt='.8rem'
            onClick={() => handleContinueClick()}>
            {t('continue')}
          </Button>
        </div>
      </ModalCrossSaleContainer>
    </ModalDialog>
  );
};

export default observer(CrossSaleModal);
